import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HouseholdStudent, SubTask, Task, TaskTypeAction } from '@k12/models';
import { HouseholdService } from '@k12/households';
import { catchError, forkJoin, map, of } from 'rxjs';

/** Structured, display-ready view of a Guardian Change (formType 10) submission. */
interface GuardianChangeReview {
  studentId: string;
  currentParentName: string;
  newParentFirstName: string;
  newParentLastName: string;
  newParentDateOfBirth: string;
  newParentPid: string;
  newParentEmail: string;
  newParentPhone: string;
  selectedStudentIds: string[];
  selectedStudents: string[];
}

/** Selected student resolved to a display name and PID (e.g. "Mark Rutter | PID: 412341232"). */
interface SelectedStudentDisplay {
  studentId: string;
  name: string;
  pid: string;
}

/**
 * Admin review of a Guardian Change request (formType 10). Renders the submitted details read-only so the
 * admin can review the request. The task itself is completed via the Status dropdown + Save in the parent
 * edit form; the guardian is changed manually by the admin outside this task.
 */
@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-guardian-change-review-form',
  imports: [CommonModule],
  templateUrl: './guardian-change-review-form.component.html',
  styleUrl: './guardian-change-review-form.component.scss'
})
export class GuardianChangeReviewFormComponent implements OnInit {
  @Input() task: Task | null = null;
  @Input() taskAction: SubTask | null = null;
  @Input() taskTypeAction: TaskTypeAction | null = null;

  private readonly householdService = inject(HouseholdService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  /** Selected students resolved to name + PID for display; the form data only stores ids/names. */
  protected selectedStudentDisplays: SelectedStudentDisplay[] = [];

  public ngOnInit(): void {
    this.resolveSelectedStudents();
  }

  /**
   * Resolves each selected student id to a display name and PID. The submitted form data only carries
   * student ids (and sometimes names), so we look up the student records to show "Name | PID: ####".
   * The list is seeded with whatever the form data provides so it renders before the lookups resolve.
   */
  private resolveSelectedStudents(): void {
    const gc = this.review;
    const ids = gc?.selectedStudentIds ?? [];
    if (ids.length === 0) {
      this.selectedStudentDisplays = [];
      return;
    }

    const fallbackNames = new Map<string, string>();
    ids.forEach((id, index) => fallbackNames.set(id, gc?.selectedStudents[index] || id));

    // Seed with form-data names so the list is visible immediately while lookups are in flight.
    this.selectedStudentDisplays = ids.map((id) => ({ studentId: id, name: fallbackNames.get(id) ?? id, pid: '' }));

    forkJoin(
      ids.map((id) =>
        this.householdService.getHouseholdStudentDetails(id).pipe(
          map((student) => ({
            studentId: id,
            name: this.buildStudentName(student) || fallbackNames.get(id) || id,
            pid: student?.pidNumber ?? ''
          })),
          catchError(() => of({ studentId: id, name: fallbackNames.get(id) ?? id, pid: '' }))
        )
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      // Upstream catchError already handles per-student failures; this stream cannot error.
      .subscribe((resolved) => {
        this.selectedStudentDisplays = resolved;
        this.cdr.markForCheck();
      });
  }

  /** Builds a student's display name from the resolved record, preferring the precomposed studentName. */
  private buildStudentName(student: HouseholdStudent | null): string {
    if (!student) {
      return '';
    }
    if (student.studentName?.trim()) {
      return student.studentName.trim();
    }
    return [student.firstName, student.middleName, student.lastName]
      .map((part) => (part ?? '').trim())
      .filter((part) => part !== '')
      .join(' ');
  }

  /**
   * Unwraps the `{ formValue: {...} }` envelope the household form saves, maps the new-guardian fields to
   * the "New Parent" labels the story uses, and resolves the selected student(s) to display names.
   * Returns null when there's no form data.
   */
  protected get review(): GuardianChangeReview | null {
    const raw = this.taskAction?.formData;
    if (!raw || typeof raw !== 'string') {
      return null;
    }

    let parsed: Record<string, unknown>;
    try {
      const result: unknown = JSON.parse(raw);
      if (!result || typeof result !== 'object') {
        return null;
      }
      parsed = result as Record<string, unknown>;
    } catch {
      return null;
    }

    // The household form persists its values under a `formValue` envelope; unwrap it if present.
    const envelope = parsed['formValue'];
    const data = (envelope && typeof envelope === 'object' ? envelope : parsed) as Record<string, unknown>;

    const asText = (value: unknown): string => (value === null || value === undefined ? '' : String(value).trim());
    const pick = (...keys: string[]): string => {
      for (const key of keys) {
        const text = asText(data[key]);
        if (text !== '') {
          return text;
        }
      }
      return '';
    };

    const buildName = (entry: Record<string, unknown>): string =>
      asText(entry['fullName']) ||
      [entry['firstName'], entry['middleName'], entry['lastName']].map(asText).filter((part) => part !== '').join(' ');

    const students = Array.isArray(data['students']) ? (data['students'] as Record<string, unknown>[]) : [];

    // Selected student ids drive the transfer; names are derived for display.
    let selectedStudentIds = students
      .filter((student) => student['selected'] === true)
      .map((student) => asText(student['studentId']))
      .filter((id) => id !== '');
    if (selectedStudentIds.length === 0 && Array.isArray(data['selectedStudentIds'])) {
      selectedStudentIds = (data['selectedStudentIds'] as unknown[]).map(asText).filter((id) => id !== '');
    }
    if (selectedStudentIds.length === 0) {
      const initiatingStudentId = asText(data['studentId']);
      if (initiatingStudentId !== '') {
        selectedStudentIds = [initiatingStudentId];
      }
    }

    const selectedStudents = selectedStudentIds.map((id) => {
      const match = students.find((student) => asText(student['studentId']) === id);
      return match ? buildName(match) || id : id;
    });

    return {
      studentId: pick('studentId'),
      currentParentName: pick('currentParentName'),
      newParentFirstName: pick('newGuardianFirstName', 'newParentFirstName'),
      newParentLastName: pick('newGuardianLastName', 'newParentLastName'),
      newParentDateOfBirth: pick('newGuardianDateOfBirth', 'newParentDateOfBirth'),
      newParentPid: pick('newGuardianPid', 'newParentPid'),
      newParentEmail: pick('newGuardianEmail', 'newParentEmail'),
      newParentPhone: pick('newGuardianPhone', 'newParentPhone'),
      selectedStudentIds,
      selectedStudents
    };
  }
}
